import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BackButton } from '../../components/BackButton';
import { FormPageShell } from '../../components/DataTablePanel';
import {
  TableIconButton,
  TableIconLink,
} from '../../components/TableIconAction';
import {
  Badge,
  BanIcon,
  Button,
  Callout,
  Card,
  DateInput,
  DocumentTextIcon,
  DownloadIcon,
  EyeIcon,
  Label,
  LoadingSpinner,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  PencilIcon,
  SaveIcon,
  Select,
  TextInput,
  Textarea,
  TrashIcon,
  UploadCloudIcon,
  XIcon,
  useConfirm,
} from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { InvoiceCategory } from '../../types/invoice';
import { ShipmentFileDetail } from '../../types/shipment';
import { formatDate, formatMoney } from '../../utils/format';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysIso(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function ShipmentFileDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const confirm = useConfirm();
  const labels = t().shipmentFiles;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<ShipmentFileDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceCategories, setInvoiceCategories] = useState<InvoiceCategory[]>(
    [],
  );
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState(todayIso());
  const [invoiceCategoryId, setInvoiceCategoryId] = useState('');
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  async function load() {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setFile(await api.getShipmentFile(Number(id)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleDelete() {
    if (!file) return;
    const ok = await confirm({
      title: labels.softDelete,
      message: labels.softDeleteConfirm,
      confirmText: labels.softDelete,
      cancelText: t().common.cancel,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await api.deleteShipmentFile(file.id);
      navigate('/shipment-files');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    }
  }

  async function handleUpload(selected: FileList | null) {
    if (!file || !selected?.[0]) return;
    setUploading(true);
    setError(null);
    try {
      await api.uploadShipmentDocument(file.id, selected[0]);
      await load();
      setMessage(t().toasts.saveSuccess);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDeleteDocument(documentId: number) {
    if (!file) return;
    try {
      await api.deleteShipmentDocument(file.id, documentId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    }
  }

  async function openInvoiceDialog() {
    if (!file) return;
    if (!file.expenses.some((e) => e.billable)) {
      setError(labels.noBillableExpenses);
      return;
    }
    const categories = await api.getInvoiceCategories(true);
    setInvoiceCategories(categories);
    const invoiceDateValue = todayIso();
    setInvoiceDate(invoiceDateValue);
    setDueDate(addDaysIso(invoiceDateValue, file.customer.creditDays ?? 0));
    setInvoiceNo('');
    setInvoiceCategoryId('');
    setInvoiceNotes(file.fileNo);
    setInvoiceError(null);
    setInvoiceOpen(true);
  }

  async function submitInvoice(event: FormEvent) {
    event.preventDefault();
    if (!file) return;
    if (!invoiceNo.trim()) {
      setInvoiceError(t().invoices.requiredInvoiceNo);
      return;
    }
    setInvoiceSaving(true);
    setInvoiceError(null);
    try {
      const updated = await api.createShipmentInvoice(file.id, {
        invoiceNo: invoiceNo.trim(),
        invoiceDate,
        dueDate,
        categoryId: invoiceCategoryId ? Number(invoiceCategoryId) : null,
        notes: invoiceNotes.trim() || undefined,
      });
      setInvoiceOpen(false);
      setMessage(labels.invoiceSuccess);
      setFile(updated);
      if (updated.createdInvoiceId) {
        navigate(`/invoices/${updated.createdInvoiceId}`);
      }
    } catch (err) {
      setInvoiceError(
        err instanceof ApiError ? err.message : t().common.error,
      );
    } finally {
      setInvoiceSaving(false);
    }
  }

  if (loading) {
    return (
      <FormPageShell wide>
        <LoadingSpinner message={t().common.loading} />
      </FormPageShell>
    );
  }

  if (!file) {
    return (
      <FormPageShell wide>
        <Callout variant="danger">{error || t().common.error}</Callout>
        <BackButton fallback="/shipment-files" />
      </FormPageShell>
    );
  }

  return (
    <FormPageShell wide>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <BackButton fallback="/shipment-files" />
          <div>
            <h1 className="text-lg font-semibold">
              {labels.details} {file.fileNo}
            </h1>
            <div className="mt-1 flex items-center gap-2">
              <Badge
                variant={file.status === 'INVOICED' ? 'success' : 'secondary'}
              >
                {labels.statuses[file.status]}
              </Badge>
              {file.locked ? (
                <span className="text-xs text-ink-muted">{labels.lockedHint}</span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="inline-flex items-center gap-0.5">
          {!file.locked ? (
            <TableIconLink
              to={`/shipment-files/${file.id}/edit`}
              label={labels.editAction}
            >
              <PencilIcon className="h-4 w-4" />
            </TableIconLink>
          ) : null}
          {file.status === 'OPEN' ? (
            <TableIconButton
              label={labels.createInvoice}
              onClick={() => void openInvoiceDialog()}
            >
              <DocumentTextIcon className="h-4 w-4" />
            </TableIconButton>
          ) : null}
          {file.invoice ? (
            <TableIconLink
              to={`/invoices/${file.invoice.id}`}
              label={labels.viewInvoice}
            >
              <EyeIcon className="h-4 w-4" />
            </TableIconLink>
          ) : null}
          {isAdmin && !file.locked ? (
            <TableIconButton
              label={labels.softDelete}
              variant="danger"
              onClick={() => void handleDelete()}
            >
              <BanIcon className="h-4 w-4" />
            </TableIconButton>
          ) : null}
        </div>
      </div>

      {error ? <Callout variant="danger">{error}</Callout> : null}
      {message ? <Callout variant="success">{message}</Callout> : null}

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="space-y-2 p-4 text-sm md:col-span-2">
          <Row label={labels.customer} value={file.customer.name} />
          <Row label={labels.blNo} value={file.blNo} />
          <Row label={labels.vessel} value={file.vessel} />
          <Row label={labels.port} value={file.port} />
          <Row
            label={labels.arrivalDate}
            value={file.arrivalDate ? formatDate(file.arrivalDate) : null}
          />
          <Row label={labels.notes} value={file.notes} />
          <Row
            label={labels.containersSection}
            value={file.containers.map((c) => c.containerNo).join(', ')}
          />
          {file.invoice ? (
            <div className="flex justify-between gap-3">
              <span className="text-ink-muted">{labels.linkedInvoice}</span>
              <Link
                to={`/invoices/${file.invoice.id}`}
                className="text-brand hover:underline"
              >
                {file.invoice.invoiceNo}
              </Link>
            </div>
          ) : null}
        </Card>

        <Card className="space-y-2 p-4 text-sm">
          <div className="text-xs uppercase tracking-wide text-ink-faint">
            {labels.totalsSection}
          </div>
          <div className="flex justify-between">
            <span>{labels.totalExpenses}</span>
            <span className="tabular-nums">
              {formatMoney(file.totals.expenseNet)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>{labels.totalVat}</span>
            <span className="tabular-nums">
              {formatMoney(file.totals.expenseVat)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>{labels.totalGross}</span>
            <span className="tabular-nums">
              {formatMoney(file.totals.expenseGross)}
            </span>
          </div>
          <div className="flex justify-between font-semibold">
            <span>{labels.billableTotal}</span>
            <span className="tabular-nums">
              {formatMoney(file.totals.billableGross)}
            </span>
          </div>
        </Card>
      </div>

      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">{labels.expensesSection}</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-muted text-left text-ink-muted">
              <tr>
                <th className="px-2 py-1">{labels.expenseCategory}</th>
                <th className="px-2 py-1">{labels.expenseInvoiceNo}</th>
                <th className="px-2 py-1">{t().financial.description}</th>
                <th className="px-2 py-1 text-right">{labels.expenseAmount}</th>
                <th className="px-2 py-1 text-right">{labels.vatRate}</th>
                <th className="px-2 py-1 text-right">{labels.vatAmount}</th>
                <th className="px-2 py-1">{t().financial.type}</th>
                <th className="px-2 py-1">{labels.billable}</th>
                <th className="px-2 py-1">{labels.containersSection}</th>
              </tr>
            </thead>
            <tbody>
              {file.expenses.map((expense) => (
                <tr key={expense.id} className="border-t border-line">
                  <td className="px-2 py-1">{expense.category.name}</td>
                  <td className="px-2 py-1">{expense.invoiceNo || '—'}</td>
                  <td className="px-2 py-1">{expense.description || '—'}</td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {formatMoney(expense.amount)}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {expense.vatRate}%
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">
                    {formatMoney(expense.vatAmount)}
                  </td>
                  <td className="px-2 py-1">
                    {expense.type === 'INVOICE'
                      ? t().financial.invoice
                      : t().financial.cash}
                  </td>
                  <td className="px-2 py-1">
                    {expense.billable ? t().common.yes : t().common.no}
                  </td>
                  <td className="px-2 py-1">
                    {expense.container?.containerNo || labels.allContainers}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">{labels.documentsSection}</h3>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
              onChange={(e) => void handleUpload(e.target.files)}
            />
            <TableIconButton
              label={labels.uploadDocument}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadCloudIcon className="h-4 w-4" />
            </TableIconButton>
          </div>
        </div>
        {file.documents.length === 0 ? (
          <p className="text-sm text-ink-muted">{labels.noDocuments}</p>
        ) : (
          <ul className="divide-y divide-line text-sm">
            {file.documents.map((doc) => (
              <li
                key={doc.id}
                className="flex flex-wrap items-center justify-between gap-2 py-2"
              >
                <div>
                  <div className="font-medium">{doc.originalName}</div>
                  <div className="text-xs text-ink-muted">
                    {formatBytes(doc.sizeBytes)} · {formatDate(doc.createdAt)}
                    {doc.uploadedBy ? ` · ${doc.uploadedBy.name}` : ''}
                  </div>
                </div>
                <div className="inline-flex items-center gap-0.5">
                  <TableIconButton
                    label={labels.downloadDocument}
                    onClick={() =>
                      void api.downloadShipmentDocument(
                        file.id,
                        doc.id,
                        doc.originalName,
                      )
                    }
                  >
                    <DownloadIcon className="h-4 w-4" />
                  </TableIconButton>
                  <TableIconButton
                    label={labels.deleteDocument}
                    variant="danger"
                    onClick={() => void handleDeleteDocument(doc.id)}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </TableIconButton>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal isOpen={invoiceOpen} onClose={() => setInvoiceOpen(false)} size="md">
        <form onSubmit={submitInvoice} className="flex min-h-0 flex-1 flex-col">
          <ModalHeader>
            <ModalTitle>{labels.invoiceDialogTitle}</ModalTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setInvoiceOpen(false)}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </ModalHeader>
          <ModalContent className="space-y-4">
            {invoiceError ? (
              <Callout variant="danger">{invoiceError}</Callout>
            ) : null}
            <div>
              <Label required>{t().invoices.invoiceNo}</Label>
              <TextInput
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
                showClearButton={false}
                autoFocus
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label required>{t().invoices.invoiceDate}</Label>
                <DateInput
                  value={invoiceDate}
                  onChange={(e) => {
                    setInvoiceDate(e.target.value);
                    setDueDate(
                      addDaysIso(
                        e.target.value,
                        file.customer.creditDays ?? 0,
                      ),
                    );
                  }}
                />
              </div>
              <div>
                <Label required>{t().invoices.dueDate}</Label>
                <DateInput
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label>{t().invoices.category}</Label>
              <Select
                value={invoiceCategoryId}
                onChange={(e) => setInvoiceCategoryId(e.target.value)}
                placeholder={t().invoices.selectCategory}
                showClearButton
                options={invoiceCategories.map((c) => ({
                  value: String(c.id),
                  label: c.name,
                }))}
              />
            </div>
            <div>
              <Label>{t().invoices.notes}</Label>
              <Textarea
                value={invoiceNotes}
                onChange={(e) => setInvoiceNotes(e.target.value)}
                rows={2}
              />
            </div>
          </ModalContent>
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setInvoiceOpen(false)}
            >
              {t().common.cancel}
            </Button>
            <Button type="submit" loading={invoiceSaving}>
              <SaveIcon className="h-4 w-4" />
              {labels.createInvoice}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </FormPageShell>
  );
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink-muted">{label}</span>
      <span className="text-right">{value || '—'}</span>
    </div>
  );
}
